import axios from 'axios';
import cookiesManager from '../utils/cookies';
import { AUTH_KEYS, USER_DETAILS } from '../utils/cookies/keys';
import { handleLogout } from '../utils/logout';
import { refreshTokenAuth } from '.';
import type { CreateApiInterface, IRefreshTokenResponse } from './hooks/login-sign-up/interface';

export const DEV_API_GW_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const NEWSTAGING_BASE_URL = import.meta.env.VITE_WELLNESS_BASE_URL;
export const HR_PORTAL_BASE_URL = import.meta.env.VITE_HR_PORTAL_API_BASE_URL;
export const TPA_BASE_URL = `${DEV_API_GW_BASE_URL}employee/tpa/`;
export const SF_BASE_URL = `${DEV_API_GW_BASE_URL}employee/sf/`;
export const BFHL_HEALTHRX_URL = import.meta.env.VITE_BFHL_HEALTHRX_URL;
export const GW_URL = import.meta.env.VITE_API_GW_URL;

let refreshPromise: Promise<undefined | IRefreshTokenResponse> | null = null;
const REFRESH_BUFFER = 2 * 60 * 1000;

interface RetryConfig {
  _retryCount?: number;
}

const createAPI = ({
  baseURL,
  enableAuth = false,
  isAppToken = false,
  headers = {},
}: CreateApiInterface) => {
  const apiHeaders = {
    ...headers,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const api = axios.create({
    baseURL,
    headers: apiHeaders,
  });

  api.interceptors.request.use(async config => {
    let accessToken = cookiesManager.get(AUTH_KEYS.ACCESS_TOKEN);
    const appToken = cookiesManager.get(AUTH_KEYS.APP_TOKEN);

    const refreshToken = cookiesManager.get(AUTH_KEYS.REFRESH_TOKEN);
    const currentTime = Date.now();
    const expiryTime = +(cookiesManager.get(AUTH_KEYS.EXPIRY_TIME) || 0);

    if (enableAuth && isAppToken) {
      if (!appToken) {
        return Promise.reject(new Error('No app token found'));
      }
      config.headers.Authorization = `Token ${appToken}`;
      return config;
    }

    if (enableAuth && accessToken) {
      if (currentTime >= expiryTime - REFRESH_BUFFER) {
        if (!refreshPromise && refreshToken) {
          refreshPromise = refreshTokenAuth({
            loginType: 'COR',
            username: cookiesManager.get(USER_DETAILS.USERNAME) ?? '',
          })
            .then(async (res: IRefreshTokenResponse) => {
              cookiesManager.setMultiple(
                [
                  {
                    key: AUTH_KEYS.ACCESS_TOKEN,
                    value: res?.data?.accessToken,
                  },
                  {
                    key: AUTH_KEYS.REFRESH_TOKEN,
                    value: res?.data?.refreshToken,
                  },
                  {
                    key: AUTH_KEYS.EXPIRY_TIME,
                    value: res?.data?.expiryTime.toString(),
                  },
                  {
                    key: AUTH_KEYS.VAPISTXID,
                    value: res?.data?.vapisTxId,
                  },
                ],
                {
                  path: '/',
                },
              );
              return res;
            })
            .catch(() => {
              handleLogout();
              return Promise.reject(new Error('Session expired. Please log in again.'));
            })
            .finally(() => {
              refreshPromise = null;
            });
        }
      }
      const data = await refreshPromise;
      accessToken = data?.data?.accessToken ?? accessToken;
      config.headers['x-authorization'] = accessToken;
    }

    return config;
  });

  api.interceptors.response.use(
    async response => {
      return response;
    },
    async error => {
      const originalRequest = error.config as RetryConfig & typeof error.config;

      if (error.response?.status === 401 && enableAuth) {
        originalRequest._retryCount = originalRequest._retryCount || 0;

        if (originalRequest._retryCount < 2) {
          originalRequest._retryCount += 1;

          try {
            if (!refreshPromise) {
              refreshPromise = refreshTokenAuth({
                loginType: 'HOS',
                username: cookiesManager.get(USER_DETAILS.USERNAME) ?? '',
              })
                .then(async (res: IRefreshTokenResponse) => {
                  cookiesManager.setMultiple(
                    [
                      {
                        key: AUTH_KEYS.ACCESS_TOKEN,
                        value: res?.data?.accessToken,
                      },
                      {
                        key: AUTH_KEYS.REFRESH_TOKEN,
                        value: res?.data?.refreshToken,
                      },
                      {
                        key: AUTH_KEYS.EXPIRY_TIME,
                        value: res?.data?.expiryTime.toString(),
                      },
                      {
                        key: AUTH_KEYS.VAPISTXID,
                        value: res?.data?.vapisTxId,
                      },
                    ],
                    {
                      path: '/',
                    },
                  );

                  return res;
                })
                .finally(() => {
                  refreshPromise = null;
                });
            }

            const data = await refreshPromise;

            originalRequest.headers['x-authorization'] = data?.data?.accessToken;

            return api(originalRequest);
          } catch (_refreshError) { }
        }

        handleLogout();
      }

      return Promise.reject(error.response?.data);
    },
  );

  return api;
};

export const devApiGwXAuthClient = createAPI({
  baseURL: DEV_API_GW_BASE_URL,
  enableAuth: true,
});

export const devApiGwNoAuth = createAPI({
  baseURL: DEV_API_GW_BASE_URL,
});

export const hrPortalApiClient = createAPI({
  baseURL: HR_PORTAL_BASE_URL,
});

export const bfhlHealthRxClient = createAPI({
  baseURL: BFHL_HEALTHRX_URL,
});

export const gatewayUrlClient = createAPI({
  baseURL: GW_URL,
  enableAuth: true,
});
